//#region src/core/composition/define-feature.ts
/**
* Define a feature. An identity function: it exists so the behavior list keeps its tuple type, which composition needs
* to derive the state, context, and config types.
*
* `defaultConfig` may cover config keys that another feature's behaviors read; the composition checks each
* `defaultConfig` value's type against the behavior that reads it.
*
* @example
*   ```ts
*   export const chaptersFeature = defineFeature({ behaviors: [loadChapters] });
*   ```;
*/
function defineFeature(feature) {
	return feature;
}
/**
* Flatten features into one behavior list, one `defaultConfig`, and one `initialState`, ready for `createComposition`
* or `defineCompositionFactory`. The feature list is the composition order.
*
* Features may share behaviors and keys. A behavior listed by several features is composed once, at its first position.
* A `defaultConfig` or `initialState` key given by several features takes the last feature's value, so a feature listed
* later can refine what an earlier one sets: DRM's playability probe replaces the plain one the video feature sets.
*
* The returned behavior type still lists a shared behavior once per feature. That's harmless: composition intersects
* the behaviors' types, and a type intersected with itself is unchanged.
*
* @example
*   ```ts
*   const { behaviors, defaultConfig, initialState } = flattenFeatures([hlsLoadingFeature, mediaSourceFeature, videoFeature]);
*   const composition = createComposition(behaviors, { defaultConfig, initialState, config });
*   ```;
*/
function flattenFeatures(features) {
	return {
		behaviors: [...new Set(features.flatMap((feature) => feature.behaviors))],
		defaultConfig: mergeFeatureValues(features, "defaultConfig"),
		initialState: mergeFeatureValues(features, "initialState")
	};
}
function mergeFeatureValues(features, key) {
	return Object.assign({}, ...features.map((feature) => feature[key] ?? {}));
}
//#endregion
export { defineFeature, flattenFeatures };
